import React, { useEffect, useState } from 'react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { Search, Filter, Download, Plus, TrendingUp, TrendingDown, CheckCircle2, Clock, AlertCircle, Info } from 'lucide-react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

export const Financials: React.FC = () => {
    const { invoices, fetchInvoices, loading, properties, fetchProperties, tenants, fetchTenants } = useLandlordStore();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [filterStatus, setFilterStatus] = useState<string>('All');
    const [selectedPropertyId, setSelectedPropertyId] = useState<string>(searchParams.get('propertyId') || 'All');

    const filterTabs = ['All', 'Paid', 'Pending', 'Overdue', 'Partially Paid'];

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'Paid': return <CheckCircle2 size={16} />;
            case 'Overdue': return <AlertCircle size={16} />;
            case 'Pending': return <Clock size={16} />;
            case 'Partially Paid': return <Info size={16} />;
            default: return <Info size={16} />;
        }
    };

    const getStatusStyles = (status: string) => {
        switch (status) {
            case 'Paid': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-500';
            case 'Overdue': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-500';
            case 'Pending': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-500';
            case 'Partially Paid': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-500';
            default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-400';
        }
    };

    useEffect(() => {
        const propertyId = searchParams.get('propertyId');
        if (propertyId) {
            setSelectedPropertyId(propertyId);
        }
    }, [searchParams]);

    useEffect(() => {
        fetchInvoices();
        if (properties.length === 0) {
            fetchProperties();
        }
        if (tenants.length === 0) {
            fetchTenants();
        }
    }, []);

    // 1. First, filter by property for the overall page metrics and charts
    const propertyFilteredInvoices = invoices.filter(inv => {
        if (selectedPropertyId === 'All') return true;

        // Infer property ID for this invoice using the tenants list
        const matchingTenant = tenants.find((t: any) =>
            t.name === inv.tenant_name && t.unitName === inv.unit_number
        ) as any;

        const inferredPropertyId = matchingTenant?.propertyId;
        return inferredPropertyId === selectedPropertyId;
    });

    // 2. Compute metrics based on property filter
    const totalRevenue = propertyFilteredInvoices.reduce((acc, inv) => acc + (inv.amount_paid || 0), 0);
    const totalPending = propertyFilteredInvoices.reduce((acc, inv) => acc + (inv.balance || 0), 0);

    // Group data by month for the chart based on property filter
    const monthlyData = propertyFilteredInvoices.reduce((acc: any[], inv) => {
        if (!inv.due_date) return acc;

        const date = new Date(inv.due_date);
        // Handle invalid dates just in case
        if (isNaN(date.getTime())) return acc;

        const month = format(date, 'MMM yyyy');
        const existing = acc.find(item => item.month === month);

        const paid = inv.amount_paid || 0;
        const unpaid = inv.balance || 0;
        const expected = inv.amount || 0;

        if (existing) {
            existing.Paid += paid;
            existing.Unpaid += unpaid;
            existing.Expected += expected;
        } else {
            acc.push({
                month,
                Paid: paid,
                Unpaid: unpaid,
                Expected: expected,
                timestamp: date.getTime()
            });
        }
        return acc;
    }, []).sort((a, b) => a.timestamp - b.timestamp);

    // 3. Second, filter the table based on search and status
    const filteredInvoices = propertyFilteredInvoices.filter(inv => {
        const matchesSearch = inv.tenant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inv.unit_number.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = filterStatus === 'All' || inv.status === filterStatus;

        return matchesSearch && matchesStatus;
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">Financials</h1>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle">Track income, invoices, and payments</p>
                </div>
                <div className="flex gap-3">
                    <select
                        value={selectedPropertyId}
                        onChange={(e) => setSelectedPropertyId(e.target.value)}
                        className="bg-light-card dark:bg-dark-card dark:bg-gray-100 dark:bg-gray-800 border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-light-bg dark:bg-dark-bg dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer hidden sm:block"
                    >
                        <option value="All">All Properties</option>
                        {properties.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                    <button className="flex items-center gap-2 bg-light-card dark:bg-dark-card dark:bg-gray-100 dark:bg-gray-800 border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-light-bg dark:bg-dark-bg dark:hover:bg-gray-700 transition-colors">
                        <Download size={18} />
                        <span className="hidden sm:inline">Export</span>
                    </button>
                    <button 
                        onClick={() => navigate('/landlord/financials/new-invoice')}
                        className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        <Plus size={18} />
                        <span className="hidden sm:inline">New Invoice</span>
                    </button>
                </div>
            </div>

            {/* Mobile Property Filter */}
            <div className="sm:hidden mb-4">
                <select
                    value={selectedPropertyId}
                    onChange={(e) => setSelectedPropertyId(e.target.value)}
                    className="w-full bg-light-card dark:bg-dark-card dark:bg-gray-100 dark:bg-gray-800 border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border text-gray-700 dark:text-gray-200 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none"
                >
                    <option value="All">All Properties</option>
                    {properties.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-light-card dark:bg-dark-card dark:bg-gray-100 dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle">Total Revenue (Collected)</h3>
                        <div className="p-2 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-lg">
                            <TrendingUp size={20} />
                        </div>
                    </div>
                    <p className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">KES {totalRevenue.toLocaleString()}</p>
                </div>
                <div className="bg-light-card dark:bg-dark-card dark:bg-gray-100 dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle">Pending Payments</h3>
                        <div className="p-2 bg-orange-100 dark:bg-orange-900/30 text-orange-600 rounded-lg">
                            <TrendingDown size={20} />
                        </div>
                    </div>
                    <p className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">KES {totalPending.toLocaleString()}</p>
                </div>
            </div>



            {/* Filters */}
            <div className="bg-light-card dark:bg-dark-card dark:bg-gray-100 dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border flex flex-col gap-4">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                    <input
                        type="text"
                        placeholder="Search invoices by tenant or unit..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                </div>
                
                {/* Horizontal Filter Chips */}
                <div className="flex overflow-x-auto pb-2 gap-2 hide-scrollbar">
                    {filterTabs.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setFilterStatus(tab)}
                            className={`whitespace-nowrap px-4 py-2 font-medium rounded-full text-sm border transition-colors ${
                                filterStatus === tab 
                                ? 'bg-primary dark:bg-golden text-white border-primary dark:border-golden' 
                                : 'bg-light-bg dark:bg-dark-bg dark:bg-gray-900 text-light-subtle dark:text-dark-subtle border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border hover:text-light-text dark:hover:text-dark-text'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* Invoices Grid - InvoiceCards mirroring Mobile */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {loading ? (
                    <div className="col-span-full py-12 text-center text-light-subtle dark:text-dark-subtle">
                        Loading financial records...
                    </div>
                ) : filteredInvoices.length === 0 ? (
                    <div className="col-span-full py-12 text-center text-light-subtle dark:text-dark-subtle bg-light-card dark:bg-dark-card dark:bg-gray-100 dark:bg-gray-800 rounded-xl border border-dashed border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border">
                        No invoices found matching your filters.
                    </div>
                ) : (
                    filteredInvoices.map((inv) => {
                        const styleClass = getStatusStyles(inv.status);
                        const statusColorClass = styleClass.split(' ')[1]; // extracts text color
                        
                        return (
                            <motion.div
                                key={inv.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`bg-light-card dark:bg-dark-card dark:bg-gray-100 dark:bg-gray-800 rounded-xl p-5 shadow-sm border-l-[5px] border-y border-r border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border hover:shadow-md transition-shadow cursor-pointer flex flex-col justify-between ${styleClass.split(' ').pop()}`}
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text dark:text-white leading-tight">
                                            {inv.tenant_name}
                                        </h3>
                                        <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">
                                            Unit: {inv.unit_number}
                                        </p>
                                        <p className="text-xs italic text-light-subtle dark:text-dark-subtle mt-0.5 opacity-80">
                                            {inv.type}
                                        </p>
                                    </div>
                                    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${styleClass.split('border-')[0]}`}>
                                        {getStatusIcon(inv.status)}
                                        <span>{inv.status}</span>
                                    </div>
                                </div>
                                
                                <div className="flex justify-between items-end pt-4 border-t border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border mt-auto">
                                    <div>
                                        <p className="text-sm text-light-subtle dark:text-dark-subtle">Amount Due</p>
                                        <p className="text-xl font-bold text-primary dark:text-golden mt-1">
                                            KES {inv.amount.toLocaleString()}
                                        </p>
                                        {inv.balance > 0 && inv.balance < inv.amount && (
                                            <p className="text-xs text-danger dark:text-danger-dark font-medium mt-1">
                                                Balance: KES {inv.balance.toLocaleString()}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-light-subtle dark:text-dark-subtle">Due Date</p>
                                        <p className="text-sm font-medium text-light-text dark:text-dark-text dark:text-white mt-1">
                                            {new Date(inv.due_date).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })
                )}
            </div>

            {/* Financial Graph */}
            <div className="bg-light-card dark:bg-dark-card dark:bg-gray-100 dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border">
                <h3 className="text-lg font-semibold text-light-text dark:text-dark-text dark:text-white mb-4">Monthly Payment Overview</h3>
                <div className="h-80 w-full">
                    {monthlyData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" className="dark:stroke-gray-700" />
                                <XAxis
                                    dataKey="month"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#6B7280', fontSize: 12 }}
                                    dy={10}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#6B7280', fontSize: 12 }}
                                    tickFormatter={(value) => `KES ${value.toLocaleString()}`}
                                />
                                <Tooltip
                                    cursor={{ fill: 'rgba(107, 114, 128, 0.05)' }}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                    formatter={(value: any) => [`KES ${Number(value || 0).toLocaleString()}`, ''] as any}
                                />
                                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                <Bar dataKey="Expected" fill="#9CA3AF" name="Expected Rent" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="Paid" fill="#10B981" name="Amount Paid" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="Unpaid" fill="#EF4444" name="Amount Unpaid" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-light-subtle dark:text-dark-subtle">
                            {loading ? "Loading financial data..." : "No financial data available to display"}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
